import { createAsyncThunk } from '@reduxjs/toolkit';
import { Case } from '../../models/case';
import { RootState } from '../store';
import { DBCaseThunks, DBKnowledgebaseThunks, DBCaseUserThunks, userSelectByQuery, userSelectOneObjectByQuery } from './DBServiceThunks';
import { JurisdictionCode } from '../../common/jurisdictions';
import { createKnowledgebaseForCase } from './KnowledgebaseUtils';
import { setCurrentCaseId } from '../slices/caseSlice';
import { setCurrentChatId } from '../slices/chatSlice';
import { setActiveSidebarTab } from '../slices/uiSlice';

/**
 * Thunk for creating a new case
 */
export const createCaseThunk = createAsyncThunk(
    'cases/create',
    async (caseData: { title: string; jurisdiction?: JurisdictionCode }, { dispatch, getState }) => {

        // Get the current user from the state
        const state = getState() as RootState;
        const user = await userSelectOneObjectByQuery(state.DBUserReducer, {});

        if (!user) {
            console.error('[CREATE_CASE_DEBUG] User not authenticated');
            throw new Error('User not authenticated');
        }

        // Create the case with the user ID as the owner
        const newCase: Partial<Case> = {
            title: caseData.title,
            ownerId: user._id,
            jurisdiction: caseData.jurisdiction
        };



        try {
            // Use the DBServiceThunks to create the case
            const createResult = dispatch(DBCaseThunks.create(newCase as Case));

            // Add a timeout wrapper to catch hanging requests
            const timeoutPromise = new Promise((_, reject) =>
                setTimeout(() => reject(new Error('CREATE_CASE_TIMEOUT: Request took longer than 10 seconds')), 10000)
            );

            const createdCase = await Promise.race([
                createResult.unwrap(),
                timeoutPromise
            ]) as Case;

            // After the case is created, create a knowledgebase for it
            if (createdCase && createdCase._id) {
                await createKnowledgebaseForCase(
                    dispatch,
                    createdCase._id,
                    user._id,
                    createdCase.title,
                    createdCase.jurisdiction,
                    {
                        throwOnError: false, // Don't throw - case creation succeeded, continue
                        logPrefix: '[CREATE_CASE_DEBUG]'
                    }
                );
            }

            // Fetch case-users (backend automatically adds creator to case-users table)
            await dispatch(DBCaseUserThunks.find({ caseId: createdCase._id }));
            dispatch(setCurrentCaseId(createdCase._id));
            dispatch(setActiveSidebarTab('chats'));

            return createdCase;
        } catch (error) {
            console.error('[CREATE_CASE_DEBUG] Failed to create case:', error);
            throw error;
        }
    }
);

/**
 * Thunk for getting all cases
 */
export const getCasesFromDBThunk = createAsyncThunk(
    'cases/get',
    async (_, { dispatch, getState }) => {
        // Get the current user from the state
        const state = getState() as RootState;
        const user = await userSelectOneObjectByQuery(state.DBUserReducer, {});

        if (!user) {
            throw new Error('User not authenticated');
        }

        // Get all cases from the database (Backend handles permissions for Owned + Shared)
        await dispatch(DBCaseThunks.find({}));

        // After cases are loaded, fetch case-users for all cases
        const updatedState = getState() as RootState;
        const caseIds = Object.keys(updatedState.DBCaseReducer.entities);

        // Fetch case-users for each case
        await Promise.all(
            caseIds.map(caseId =>
                dispatch(DBCaseUserThunks.find({ caseId }))
            )
        );
    }
);


export const setNewCurrentCaseThunk = createAsyncThunk(
    'cases/setCurrent',
    async (arg: string | { caseId: string, initialChatId?: string }, { dispatch }) => {
        const caseId = typeof arg === 'string' ? arg : arg.caseId;
        const initialChatId = typeof arg === 'string' ? undefined : arg.initialChatId;

        // Fetch knowledgebases and case-users for this case
        await dispatch(DBKnowledgebaseThunks.find({ caseId }));
        await dispatch(DBCaseUserThunks.find({ caseId }));
        await dispatch(setCurrentCaseId(caseId));

        // If initialChatId is provided, set it. 
        // If not provided AND arg is a string (legacy/UI click), clear it.
        // If arg is object but initialChatId is undefined, we assume caller handles chat (or we want to keep it? No, usually we want to clear if switching cases)
        // Actually, per plan: 
        // "If called with a string (legacy/UI click), clear chatId (behavior unchanged)."
        // "If called with an object and initialChatId is provided, set chatId to that value."
        // "If called with an object and initialChatId is missing, do not clear chatId (let UrlSync handle it)." -> Wait, if UrlSync calls it with just caseId, it means case changed but no chat.

        // Let's stick to the plan:
        if (typeof arg === 'string') {
            await dispatch(setCurrentChatId(null));
        } else if (arg.initialChatId) {
            await dispatch(setCurrentChatId(arg.initialChatId));
        }
        // If arg is object and no initialChatId, we do nothing to chatId (preserve it or let others handle it)

        await dispatch(setActiveSidebarTab('chats'));
    }
);

/**
 * Thunk for deleting a case and clearing currentCaseId if it matches the deleted case
 */
export const deleteCaseThunk = createAsyncThunk(
    'cases/delete',
    async (caseId: string, { dispatch, getState }) => {
        // Get the current state
        const state = getState() as RootState;
        const currentCaseId = state.CaseReducer.currentCaseId;

        // Delete the case using the existing DB service
        await dispatch(DBCaseThunks.delete({ entity: { _id: caseId } }));

        // If the deleted case was the currently selected case, clear the currentCaseId
        if (currentCaseId === caseId) {
            dispatch(setCurrentCaseId(''));
        }
    }
);

